import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import PricingPlansSection from '../components/PricingPlansSection';
import { REGISTER_URL } from '../lib/authLinks';

const TradingPage = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    // Scroll to details section after a short delay
    setTimeout(() => {
      document.getElementById('details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const renderDetailedContent = () => {
    const content = {
      'how-to-trade': {
        title: 'How to Trade',
        description: 'Learn the basics of trading with PreciseFx',
        details: [
          { step: '1', title: 'Create Your Account', text: 'Sign up in less than 2 minutes with your email and verify your identity.' },
          { step: '2', title: 'Choose Your Asset', text: 'Select from forex, stocks, commodities, indices, or cryptocurrencies.' },
          { step: '3', title: 'Let AI Analyze', text: 'PreciseFx\'s advanced algorithms analyze market conditions automatically.' },
          { step: '4', title: 'AI Executes Trades', text: 'Our AI automatically executes trades with optimal timing — no manual entries required.' },
          { step: '5', title: 'Monitor & Close', text: 'Track your open positions and close them when you reach your profit target.' }
        ]
      },
      'stocks': {
        title: 'Stocks Trading',
        description: 'Trade blue-chip stocks with zero commission',
        details: [
          { title: 'Zero Commission', text: 'Trade stocks without paying any commission fees. Keep more of your profits.' },
          { title: '100+ Stocks', text: 'Access major companies like Apple, Google, Amazon, Tesla, and more.' },
          { title: 'Real-time Quotes', text: 'Get live market data and execute trades instantly.' },
          { title: 'Fractional Shares', text: 'Start trading with as little as $10, no need to buy full shares.' },
          { title: 'Global Markets', text: 'Trade stocks from US, European, and Asian markets.' }
        ]
      },
      'demo-account': {
        title: 'AI Auto Trading',
        description: 'Trade automatically using advanced AI technology',
        details: [
          { title: 'Smart Trading Algorithms', text: 'Our AI monitors market trends 24/7 and executes trades with precision — no emotions, no delays.' },
          { title: 'Real Market Execution', text: 'Every trade is executed in real markets with live pricing and high-speed order processing.' },
          { title: 'Risk-Managed Strategies', text: 'Built-in safety measures help protect your capital using proven risk-management techniques.' },
          { title: 'Full Control, Zero Effort', text: 'Set your preferences once — the AI handles the rest while you track your performance anytime.' },
          { title: 'One-Click Activation', text: 'Enable AI trading instantly with a single click and start earning without active involvement.' }
        ]
      },
      'india': {
        title: 'PreciseFx for India',
        description: 'Special benefits for Indian traders',
        details: [
          { title: 'Local Payment Methods', text: 'Deposit and withdraw using UPI, Paytm, PhonePe, and Indian bank transfers.' },
          { title: 'INR Support', text: 'Trade directly in Indian Rupees without currency conversion fees.' },
          { title: 'Indian Market Hours', text: 'Trade during convenient hours aligned with Indian time zones.' },
          { title: 'Local Support', text: '24/7 customer support in Hindi and English.' },
          { title: 'Compliance', text: 'Fully compliant with Indian trading regulations and guidelines.' }
        ]
      },
      'fixed-time': {
        title: 'AI-Powered Smart Trading',
        description: 'Automated trading with intelligent market timing',
        details: [
          { title: 'Minimum Investment $100', text: 'Start automated trading with just $100 — ideal for smart investors entering the AI trading world.' },
          { title: 'Smart Market Timing', text: 'No fixed duration! The AI opens and closes trades based on live Stock & Forex market conditions and timing, ensuring the best opportunities.' },
          { title: 'AI-Driven Decisions', text: 'Advanced algorithms analyze market trends and execute trades automatically for optimal results.' },
          { title: 'Zero Manual Work', text: 'Fully automated system — no charts, no manual entries, no experience required.' },
          { title: 'Risk Management', text: 'Built-in risk controls and capital safety features protect your investment.' }
        ]
      },
      'account': {
        title: 'Trading Accounts',
        description: 'Choose the account type that suits you',
        details: [
          { title: 'Demo Account', text: 'Practice with $10,000 virtual funds, no risk involved.' },
          { title: 'Standard Account', text: 'Start live trading with a minimum deposit of just $10.' },
          { title: 'VIP Account', text: 'Get exclusive benefits, personal manager, and higher returns.' },
          { title: 'Crypto Account', text: 'Trade and deposit using cryptocurrencies like Bitcoin and Ethereum.' },
          { title: 'Multi-Currency', text: 'Hold and trade in multiple currencies including USD, EUR, INR.' }
        ]
      },
      'flex': {
        title: 'Flex Trading',
        description: 'Flexible trading mechanics for advanced traders',
        details: [
          { title: 'Flexible Closing', text: 'Close your trades early to lock in profits or minimize losses.' },
          { title: 'Partial Close', text: 'Close part of your position while keeping the rest open.' },
          { title: 'Rollover Option', text: 'Extend your trade duration if you need more time.' },
          { title: 'Dynamic Returns', text: 'Returns adjust based on market conditions and timing.' },
          { title: 'Advanced Control', text: 'Full control over your trading strategy and risk management.' }
        ]
      },
      'withdrawals': {
        title: 'Withdrawals',
        description: 'Fast and secure withdrawal process',
        details: [
          { title: 'Quick Processing', text: 'Withdrawals processed within 1-3 business days.' },
          { title: 'Multiple Methods', text: 'Withdraw via bank transfer, e-wallets, cards, or crypto.' },
          { title: 'No Hidden Fees', text: 'Transparent fee structure, no surprise charges.' },
          { title: 'Secure Transactions', text: 'Bank-level security for all withdrawal transactions.' },
          { title: 'Minimum Withdrawal', text: 'Withdraw as little as $10 from your account.' }
        ]
      },
      'forex': {
        title: 'Forex Trading',
        description: 'Trade currency pairs 24/5',
        details: [
          { title: '70+ Currency Pairs', text: 'Trade major, minor, and exotic forex pairs.' },
          { title: '24/5 Trading', text: 'Markets open 24 hours a day, 5 days a week.' },
          { title: 'Low Spreads', text: 'Competitive spreads starting from 0.1 pips.' },
          { title: 'High Leverage', text: 'Trade with leverage up to 1:1000 for maximum potential.' },
          { title: 'Classic Mode', text: 'Perfect for both short-term scalping and long-term position trading.' }
        ]
      },
      'islamic': {
        title: 'Islamic Account',
        description: 'Sharia-compliant trading account',
        details: [
          { title: 'Swap-Free Trading', text: 'No overnight interest charges, fully halal and Sharia-compliant.' },
          { title: 'All Assets Available', text: 'Trade forex, stocks, commodities, and indices.' },
          { title: 'Same Features', text: 'Access all platform features available in standard accounts.' },
          { title: 'Instant Execution', text: 'Fast order execution with no delays.' },
          { title: 'Halal Certified', text: 'Verified by Islamic scholars as compliant with Islamic finance principles.' }
        ]
      },
      'promotions': {
        title: 'Promotions & Bonuses',
        description: 'Exclusive offers for our traders',
        details: [
          { title: 'Welcome Bonus', text: 'Get up to 100% bonus on your first deposit.' },
          { title: 'Loyalty Program', text: 'Earn points with every trade and redeem for cash rewards.' },
          { title: 'Cashback Offers', text: 'Get cashback on your trading volume every month.' },
          { title: 'Referral Bonus', text: 'Invite friends and earn rewards for each successful referral.' },
          { title: 'VIP Benefits', text: 'Exclusive tournaments, higher returns, and personal account manager.' }
        ]
      }
    };

    const selected = content[selectedOption];
    if (!selected) return null;

    return (
      <div>
        <h2 className="text-4xl md:text-5xl font-bold mb-4">{selected.title}</h2>
        <p className="text-xl text-gray-400 mb-12">{selected.description}</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          {selected.details.map((detail, index) => (
            <div key={index} className="rounded-xl p-6 border border-gray-800">
              {detail.step && (
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl mb-4">
                  {detail.step}
                </div>
              )}
              <h3 className="text-xl font-bold mb-2 text-yellow-500">{detail.title}</h3>
              <p className="text-gray-300">{detail.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg px-8 py-6 rounded-xl">
              Start Trading Now
            </Button>
          </a>
        </div>
      </div>
    );
  };

  const tradingInstruments = [
    {
      icon: '💱',
      title: 'Forex',
      description: 'Trade major, minor, and exotic currency pairs',
      features: ['70+ Currency Pairs', '24/5 Trading', 'Low Spreads', 'High Liquidity'],
      profitability: 'Up to 93%'
    },
    {
      icon: '📈',
      title: 'Stocks',
      description: 'Access global stock markets and leading companies',
      features: ['100+ Stocks', 'Real-time Quotes', 'No Commission', 'Fractional Shares'],
      profitability: 'Up to 90%'
    },
    {
      icon: '🏆',
      title: 'Commodities',
      description: 'Trade gold, silver, oil, and other commodities',
      features: ['Gold & Silver', 'Oil & Gas', 'Agricultural', 'Metals'],
      profitability: 'Up to 88%'
    },
    {
      icon: '📊',
      title: 'Indices',
      description: 'Trade major global stock indices',
      features: ['S&P 500', 'NASDAQ', 'FTSE 100', 'DAX 30'],
      profitability: 'Up to 85%'
    },
    {
      icon: '₿',
      title: 'Cryptocurrencies',
      description: 'Trade Bitcoin, Ethereum, and other digital assets',
      features: ['Bitcoin', 'Ethereum', 'Litecoin', '24/7 Trading'],
      profitability: 'Up to 92%'
    },
    {
      icon: '⚡',
      title: 'Fixed Time Trades',
      description: 'Fast-paced trading with fixed returns',
      features: ['5s to 23h', 'Fixed Returns', 'Simple Interface', 'Quick Profits'],
      profitability: 'Up to 93%'
    }
  ];

  const tradingFeatures = [
    {
      icon: '💰',
      title: 'Low Minimum Deposit',
      description: 'Start trading with just $10'
    },
    {
      icon: '📱',
      title: 'Trade Anywhere',
      description: 'Web and mobile platforms'
    },
    {
      icon: '🎓',
      title: 'Free Education',
      description: 'Tutorials, webinars, and market analysis'
    },
    {
      icon: '🛡️',
      title: 'Risk Management',
      description: 'Stop loss, take profit, and risk-free trades'
    },
    {
      icon: '⚡',
      title: 'Fast Execution',
      description: 'Lightning-fast order execution'
    },
    {
      icon: '💳',
      title: 'Multiple Payment Methods',
      description: 'Cards, e-wallets, and local options'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/20 to-black animate-pulse"></div>
          <div className={`relative z-10 max-w-7xl mx-auto px-6 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-down">
              Start Trading with <span className="text-yellow-500">PreciseFx</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8 animate-fade-in-up animate-delay-200">
              Access global markets with competitive spreads, advanced tools, and 24/7 support
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animate-delay-400">
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg px-8 py-6 rounded-xl hover-scale animate-glow">
                  Start Trading Now
                </Button>
              </a>
              <a href="#pricing-plans">
                <Button className="bg-gray-900 hover:bg-gray-800 text-white font-bold text-lg px-8 py-6 rounded-xl border border-gray-700 hover-scale">
                  View Trading Plans
                </Button>
              </a>
            </div>
          </div>
        </section>



        {/* Trading Features */}
        <section className="bg-gradient-to-b from-black via-gray-900 to-black py-20">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 animate-scale-in">
              Why Trade with <span className="text-yellow-500">Us</span>
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tradingFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 text-center border border-gray-800 hover-lift hover-glow animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="text-5xl mb-4 animate-float">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Start Trading */}
        <section className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 animate-scale-in">
            How to <span className="text-yellow-500">Start Trading</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center animate-fade-in-up">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black text-3xl font-bold hover-scale animate-glow">
                1
              </div>
              <h3 className="text-xl font-bold mb-2">Create Account</h3>
              <p className="text-gray-400">Sign up in less than 2 minutes</p>
            </div>

            <div className="text-center animate-fade-in-up animate-delay-100">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black text-3xl font-bold hover-scale animate-glow">
                2
              </div>
              <h3 className="text-xl font-bold mb-2">Verify & Deposit</h3>
              <p className="text-gray-400">Verify your account and add funds</p>
            </div>

            <div className="text-center animate-fade-in-up animate-delay-200">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black text-3xl font-bold hover-scale animate-glow">
                3
              </div>
              <h3 className="text-xl font-bold mb-2">Choose Asset</h3>
              <p className="text-gray-400">Select from 100+ trading instruments</p>
            </div>

            <div className="text-center animate-fade-in-up animate-delay-300">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black text-3xl font-bold hover-scale animate-glow">
                4
              </div>
              <h3 className="text-xl font-bold mb-2">Start Trading</h3>
              <p className="text-gray-400">Place your first trade and earn profits</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-5xl mx-auto px-6 py-20">
          <div className="bg-gradient-to-r from-yellow-900/30 to-gray-900 rounded-2xl p-12 text-center hover-glow animate-scale-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in-down">
              Ready to Start Trading?
            </h2>
            <p className="text-xl text-gray-300 mb-8 animate-fade-in-up animate-delay-200">
              Join millions of traders worldwide and start your trading journey today
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animate-delay-400">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg px-8 py-6 rounded-xl hover-scale animate-glow">
                Open Live Account
              </Button>
              <Link to="/help">
                <Button className="bg-transparent hover:bg-gray-800 text-white font-bold text-lg px-8 py-6 rounded-xl border-2 border-yellow-500 hover-scale">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Pricing Plans Section */}
        <PricingPlansSection />
      </main>

      <Footer />
    </div>
  );
};

export default TradingPage;
